import { useEffect } from 'react';
import { useBackgroundColor } from '@tennr/frontend/context/BackgroundColorContext';
import { getLuminance } from '@tennr/frontend/utils/getLuminance';
import { rgbToHex } from '@tennr/frontend/utils/rgbToHex';

type StatusBarStyle = 'black' | 'white';

export interface ThemeColorOverride {
  color: string;
  statusBarStyle: StatusBarStyle;
}

/**
 * Hook to sync Safari status bar color with background transitions
 * @param override - Optional override for theme color and status bar style
 */
export function useThemeColor(override?: ThemeColorOverride) {
  const { currentColor } = useBackgroundColor();

  useEffect(() => {
    const hexColor = override?.color || rgbToHex(currentColor[0], currentColor[1], currentColor[2]);

    // Find or create meta tag
    let metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta');
      metaThemeColor.setAttribute('name', 'theme-color');
      document.head.appendChild(metaThemeColor);
    }

    // Update the theme color
    metaThemeColor.setAttribute('content', hexColor);

    // Also update the status bar style for iOS 15+
    let metaStatusBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    if (!metaStatusBar) {
      metaStatusBar = document.createElement('meta');
      metaStatusBar.setAttribute('name', 'apple-mobile-web-app-status-bar-style');
      document.head.appendChild(metaStatusBar);
    }

    // Use override status bar style or calculate based on luminance
    let statusBarStyle: StatusBarStyle;
    if (override?.statusBarStyle) {
      statusBarStyle = override.statusBarStyle;
    } else {
      const luminance = getLuminance(currentColor[0], currentColor[1], currentColor[2]);
      statusBarStyle = luminance > 0.5 ? 'black' : 'white';
    }
    metaStatusBar.setAttribute('content', statusBarStyle);

  }, [currentColor, override]);
}